var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { LOGIN_CODE_LENGTH } from '@common/contracts';
import { sendLoginCode, verifyLoginCode } from '../../api/auth';
import { setSessionToken } from '../../auth/session';
import { getIdentity, setIdentity } from '../../auth/identity';
import heroDesktop from './assets/login-hero-desktop.jpg';
import heroMobile from './assets/login-hero-mobile.jpg';
import './Login.scss';
/** Pulls the backend's `{ error }` envelope out of an axios failure. */
function errorMessage(error) {
    var _a, _b;
    return ((_b = (_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.error) || 'Something went wrong. Please try again.';
}
// Below this width we show the "open on desktop" card instead of the login form —
// the level editor itself isn't usable on phones.
const MOBILE_QUERY = '(max-width: 768px)';
function useIsMobile() {
    const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia(MOBILE_QUERY).matches);
    useEffect(() => {
        const mql = window.matchMedia(MOBILE_QUERY);
        const onChange = () => setIsMobile(mql.matches);
        mql.addEventListener('change', onChange);
        return () => mql.removeEventListener('change', onChange);
    }, []);
    return isMobile;
}
// Vision Makers login: a glassmorphic card over full-bleed hero art. On desktop
// it runs the username → code flow; on mobile (where the editor doesn't work) it
// offers to copy the current link so the user can reopen it on a computer.
export function Login({ onLoggedIn }) {
    const isMobile = useIsMobile();
    const [step, setStep] = useState('username');
    const [username, setUsername] = useState('');
    const [code, setCode] = useState('');
    const [pending, setPending] = useState(false);
    const [error, setError] = useState(null);
    const [copied, setCopied] = useState(false);
    const [leaving, setLeaving] = useState(false);
    const handleSendCode = (e) => __awaiter(this, void 0, void 0, function* () {
        e.preventDefault();
        if (pending || !username.trim())
            return;
        setPending(true);
        setError(null);
        try {
            yield sendLoginCode(username);
            setCode('');
            setStep('code');
        }
        catch (err) {
            setError(errorMessage(err));
        }
        finally {
            setPending(false);
        }
    });
    const handleVerifyCode = (e) => __awaiter(this, void 0, void 0, function* () {
        e.preventDefault();
        if (pending || !code.trim())
            return;
        setPending(true);
        setError(null);
        try {
            const result = yield verifyLoginCode(username, code);
            setSessionToken(result.token);
            // The verified username replaces whatever the mock switcher held.
            setIdentity(Object.assign(Object.assign({}, getIdentity()), { username: result.username }));
            // Play the exit animation, then hand off to the app.
            setLeaving(true);
            window.setTimeout(() => onLoggedIn(result.username), 380);
        }
        catch (err) {
            setError(errorMessage(err));
            setPending(false);
        }
    });
    const handleBack = () => {
        setStep('username');
        setCode('');
        setError(null);
    };
    const handleCopyLink = () => __awaiter(this, void 0, void 0, function* () {
        const link = window.location.href;
        try {
            yield navigator.clipboard.writeText(link);
        }
        catch (_a) {
            // Older browsers / insecure contexts: fall back to a temporary textarea.
            const area = document.createElement('textarea');
            area.value = link;
            document.body.appendChild(area);
            area.select();
            try {
                document.execCommand('copy');
            }
            catch ( /* nothing else to try */_b) { /* nothing else to try */ }
            document.body.removeChild(area);
        }
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
    });
    return (_jsxs("div", { className: `login${leaving ? ' login--leaving' : ''}`, children: [_jsx("img", { className: "login__hero", src: isMobile ? heroMobile : heroDesktop, alt: "", "aria-hidden": true }), _jsxs("div", { className: "login__card", children: [_jsxs("div", { className: "login__title-block", children: [_jsx("h1", { className: "login__title", children: "Welcome to the Vision Makers!" }), _jsx("p", { className: "login__subtitle", children: "Stalker, shape your own Simulations - filled with resources, challenges, and hidden rewards." })] }), isMobile ? (_jsxs("button", { type: "button", className: "login__cta", onClick: handleCopyLink, children: [copied ? 'Link copied!' : 'Copy Link', copied ? _jsx(CheckIcon, {}) : _jsx(CopyIcon, {})] })) : step === 'username' ? (_jsxs("form", { className: "login__form", onSubmit: handleSendCode, children: [_jsxs("div", { className: "login__input", children: [_jsx(SendIcon, { className: "login__input-icon" }), _jsx("input", { type: "text", name: "vm-handle", value: username, placeholder: "Enter Telegram username", autoFocus: true, autoComplete: "off", autoCorrect: "off", autoCapitalize: "none", spellCheck: false, onChange: (e) => setUsername(e.target.value) })] }), _jsxs("button", { type: "submit", className: "login__cta", disabled: pending || !username.trim(), children: [pending ? 'Sending…' : 'Get code', _jsx(ArrowIcon, {})] })] }, "username")) : (_jsxs("form", { className: "login__form login__form--code", onSubmit: handleVerifyCode, children: [_jsxs("p", { className: "login__hint", children: ["We sent a code to ", _jsxs("b", { children: ["@", username.trim().replace(/^@/, '')] }), " in Telegram."] }), _jsx("div", { className: "login__input", children: _jsx("input", { type: "text", name: "vm-code", inputMode: "numeric", maxLength: LOGIN_CODE_LENGTH, value: code, placeholder: "Enter the 6-digit code", autoFocus: true, autoComplete: "off", autoCorrect: "off", autoCapitalize: "none", spellCheck: false, onChange: (e) => setCode(e.target.value.replace(/\D/g, '')) }) }), _jsxs("button", { type: "submit", className: "login__cta", disabled: pending || code.length !== LOGIN_CODE_LENGTH, children: [pending ? 'Checking…' : 'Log in', _jsx(ArrowIcon, {})] }), _jsx("button", { type: "button", className: "login__back", onClick: handleBack, disabled: pending, children: "Use another username" })] }, "code")), error && _jsx("div", { className: "login__error", children: error })] })] }));
}
// --- Inline icons. Drawn in a 32×32 box to match the Figma icon slots. ---
function ArrowIcon() {
    return (_jsx("svg", { className: "login__cta-icon", viewBox: "0 0 32 32", fill: "none", "aria-hidden": true, children: _jsx("path", { d: "M7.5 16h16M17 9.5l7 6.5-7 6.5", stroke: "currentColor", strokeWidth: "2.4", strokeLinecap: "round", strokeLinejoin: "round" }) }));
}
function CopyIcon() {
    return (_jsxs("svg", { className: "login__cta-icon", viewBox: "0 0 32 32", fill: "none", "aria-hidden": true, children: [_jsx("rect", { x: "12", y: "12", width: "14", height: "14", rx: "3.5", stroke: "currentColor", strokeWidth: "2.2" }), _jsx("path", { d: "M8 20H7a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3h10a3 3 0 0 1 3 3v1", stroke: "currentColor", strokeWidth: "2.2", strokeLinecap: "round" })] }));
}
function CheckIcon() {
    return (_jsx("svg", { className: "login__cta-icon", viewBox: "0 0 32 32", fill: "none", "aria-hidden": true, children: _jsx("path", { d: "M7 16.5l6 6L25 9", stroke: "currentColor", strokeWidth: "2.6", strokeLinecap: "round", strokeLinejoin: "round" }) }));
}
function SendIcon({ className }) {
    return (_jsxs("svg", { className: className, viewBox: "0 0 32 32", fill: "none", "aria-hidden": true, children: [_jsx("path", { d: "M28 4L4 13l9 4 4 10L28 4z", stroke: "currentColor", strokeWidth: "1.9", strokeLinecap: "round", strokeLinejoin: "round" }), _jsx("path", { d: "M28 4L13 17", stroke: "currentColor", strokeWidth: "1.9", strokeLinecap: "round", strokeLinejoin: "round" })] }));
}
export default Login;
